import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Repeat, ArrowUpRight, ArrowDownRight, ChevronRight, Calendar } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MoneyAmount from "@/components/MoneyAmount";

// Normalise any frequency to a monthly-equivalent amount for aggregate display.
const MONTHLY_MULTIPLIER = { daily: 30, weekly: 4.33, monthly: 1, yearly: 1 / 12 };

function relativeDay(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  d.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff < 0) return `${-diff}d ago`;
  if (diff <= 7) return `in ${diff}d`;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/**
 * Compact premium Recurring Overview card.
 * Shows monthly-equivalent recurring income, expense, net, and up to 3 upcoming items.
 */
export default function RecurringOverview({ refreshKey = 0 }) {
  const { user } = useAuth();
  const currency = user?.currency || "USD";
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/recurring");
      setItems(data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [refreshKey]);

  const { incomeTotal, expenseTotal, upcoming } = useMemo(() => {
    let inc = 0;
    let exp = 0;
    const active = items.filter((r) => r.active !== false);
    for (const r of active) {
      const mult = MONTHLY_MULTIPLIER[r.frequency] ?? 1;
      const monthly = Number(r.amount || 0) * mult;
      if (r.kind === "income") inc += monthly;
      else exp += monthly;
    }
    const sorted = active
      .filter((r) => r.next_run)
      .sort((a, b) => (a.next_run > b.next_run ? 1 : -1))
      .slice(0, 3);
    return { incomeTotal: inc, expenseTotal: exp, upcoming: sorted };
  }, [items]);

  const net = incomeTotal - expenseTotal;

  if (!loading && items.length === 0) {
    // Compact empty state — keeps the card slot but doesn't push layout
    return (
      <div className="pcard pcard-hover p-5 md:p-6" data-testid="recurring-overview-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-xl text-white"
              style={{
                background: "linear-gradient(160deg,#1E3F33 0%,#4A6B53 100%)",
                boxShadow: "0 8px 18px -10px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.28)",
              }}
            >
              <Repeat className="h-4 w-4" strokeWidth={2.2} />
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Recurring Overview</div>
              <div className="text-sm text-muted-foreground">No recurring items yet — add a subscription or salary to auto-track it.</div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate("/recurring")}
            className="inline-flex items-center gap-1 rounded-full border border-border px-3.5 py-1.5 text-[12.5px] font-semibold text-foreground transition hover:bg-muted/60"
            data-testid="recurring-overview-view-all"
          >
            Add
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pcard pcard-hover p-5 md:p-6" data-testid="recurring-overview-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div
            className="flex h-9 w-9 items-center justify-center rounded-xl text-white"
            style={{
              background: "linear-gradient(160deg,#1E3F33 0%,#4A6B53 100%)",
              boxShadow: "0 8px 18px -10px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.28)",
            }}
          >
            <Repeat className="h-4 w-4" strokeWidth={2.2} />
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Recurring Overview</div>
            <div className="text-[13px] text-muted-foreground">Fixed monthly recurring income and expenses.</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate("/recurring")}
          className="inline-flex items-center gap-1 rounded-full border border-border px-3.5 py-1.5 text-[12.5px] font-semibold text-foreground transition hover:bg-muted/60"
          data-testid="recurring-overview-view-all"
        >
          View All Recurring
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Totals row */}
      <div className="mt-4 grid grid-cols-3 gap-3">
        <div className="rounded-2xl border border-border/70 bg-muted/30 p-3">
          <div className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            <ArrowUpRight className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
            Income
          </div>
          <div className="mt-1 font-display text-[17px] font-extrabold tabular-nums text-emerald-700 dark:text-emerald-400" data-testid="recurring-income-total">
            <MoneyAmount value={incomeTotal} currency={currency} tone="neutral" />
          </div>
        </div>
        <div className="rounded-2xl border border-border/70 bg-muted/30 p-3">
          <div className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            <ArrowDownRight className="h-3 w-3 text-[#B91C1C] dark:text-[#FCA5A5]" />
            Expense
          </div>
          <div className="mt-1 font-display text-[17px] font-extrabold tabular-nums text-[#B91C1C] dark:text-[#FCA5A5]" data-testid="recurring-expense-total">
            <MoneyAmount value={expenseTotal} currency={currency} tone="neutral" />
          </div>
        </div>
        <div className="rounded-2xl border border-border/70 bg-muted/30 p-3">
          <div className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Net</div>
          <div
            className={`mt-1 font-display text-[17px] font-extrabold tabular-nums ${net >= 0 ? "text-foreground" : "text-[#B91C1C] dark:text-[#FCA5A5]"}`}
            data-testid="recurring-net-total"
          >
            <MoneyAmount value={net} currency={currency} tone="auto" />
          </div>
        </div>
      </div>

      {/* Upcoming */}
      {upcoming.length > 0 && (
        <div className="mt-4">
          <div className="mb-2 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            <Calendar className="h-3 w-3" />
            Upcoming
          </div>
          <ul className="space-y-1.5" data-testid="recurring-upcoming-list">
            {upcoming.map((r) => {
              const isIncome = r.kind === "income";
              return (
                <li
                  key={r.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-border/60 bg-background/40 px-3 py-2 backdrop-blur-sm"
                  data-testid="recurring-upcoming-item"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={`inline-block h-2 w-2 shrink-0 rounded-full ${isIncome ? "bg-emerald-500" : "bg-[#EF4444]"}`}
                      aria-hidden
                    />
                    <span className="truncate text-[13px] font-semibold text-foreground">
                      {r.label || (isIncome ? "Income" : "Expense")}
                    </span>
                    <span className="shrink-0 text-[11px] text-muted-foreground">· {relativeDay(r.next_run)}</span>
                  </div>
                  <span className={`shrink-0 font-display text-[13px] font-bold tabular-nums ${isIncome ? "text-emerald-700 dark:text-emerald-400" : "text-[#B91C1C] dark:text-[#FCA5A5]"}`}>
                    <MoneyAmount value={isIncome ? Number(r.amount || 0) : -Number(r.amount || 0)} currency={currency} tone="neutral" signed />
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

